import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LocalizedText } from '@cr/shared';
import { api, errorMessage } from '@/lib/api';
import { localized, money } from '@/lib/format';
import { useSession } from '@/lib/session';
import { haptic, setMainButton } from '@/lib/telegram';
import { Button, Card, Checkbox, Chip, EmptyState, Row, Screen, Skeleton } from '@/components/ui';

type Fulfillment = 'DELIVERY' | 'PICKUP';
type Payment = 'CLICK' | 'PAYME' | 'CASH';

interface Address {
  id: string;
  label: string | null;
  addressLine: string;
  distanceKm: number | null;
  isDefault: boolean;
}

interface PickupPoint {
  id: string;
  name: LocalizedText;
  address: LocalizedText;
  phone: string | null;
}

interface Preview {
  subtotal: number;
  promoDiscount: number;
  deliveryCost: number;
  depositTotal: number;
  depositPayable: number;
  depositMode: string;
  payableNow: number;
  total: number;
  distanceKm: number | null;
  deliveryAvailable: boolean;
  deliveryReason?: string;
  promo: { valid: boolean; reason?: string; code?: string } | null;
  canCheckout: boolean;
}

export default function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authorized, insideTelegram } = useSession();

  const [fulfillment, setFulfillment] = useState<Fulfillment>('DELIVERY');
  const [payment, setPayment] = useState<Payment>('CLICK');
  const [addressId, setAddressId] = useState<string | null>(null);
  const [pickupPointId, setPickupPointId] = useState<string | null>(null);
  const [promoInput, setPromoInput] = useState('');
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [offerAccepted, setOfferAccepted] = useState(false);
  const [showOffer, setShowOffer] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addresses = useQuery({
    queryKey: ['addresses'],
    queryFn: async () => (await api.get<Address[]>('/addresses')).data,
    enabled: authorized,
  });

  const points = useQuery({
    queryKey: ['pickup-points'],
    queryFn: async () => (await api.get<PickupPoint[]>('/catalog/pickup-points')).data,
  });

  const offer = useQuery({
    queryKey: ['offer'],
    queryFn: async () =>
      (await api.get<{ version: number; content: LocalizedText }>('/catalog/offer')).data,
  });

  // Первый адрес и первый пункт выбираются сами — на чекауте лишний тап дорог
  useEffect(() => {
    if (!addressId && addresses.data?.length) {
      setAddressId((addresses.data.find((a) => a.isDefault) ?? addresses.data[0]!).id);
    }
  }, [addresses.data, addressId]);

  useEffect(() => {
    if (!pickupPointId && points.data?.length) setPickupPointId(points.data[0]!.id);
  }, [points.data, pickupPointId]);

  const preview = useQuery({
    queryKey: ['order-preview', fulfillment, payment, addressId, pickupPointId, promoCode],
    enabled: authorized,
    queryFn: async () =>
      (
        await api.post<Preview>('/orders/preview', {
          fulfillmentType: fulfillment,
          paymentMethod: payment,
          addressId: fulfillment === 'DELIVERY' ? (addressId ?? undefined) : undefined,
          pickupPointId: fulfillment === 'PICKUP' ? (pickupPointId ?? undefined) : undefined,
          promoCode: promoCode ?? undefined,
        })
      ).data,
  });

  const createAddress = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post<Address>('/addresses', payload),
    onSuccess: (res) => {
      setShowAddressForm(false);
      setAddressId(res.data.id);
      void queryClient.invalidateQueries({ queryKey: ['addresses'] });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const submit = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ id: string; number: string }>('/orders', {
          fulfillmentType: fulfillment,
          paymentMethod: payment,
          addressId: fulfillment === 'DELIVERY' ? addressId : undefined,
          pickupPointId: fulfillment === 'PICKUP' ? pickupPointId : undefined,
          promoCode: promoCode ?? undefined,
          comment: comment || undefined,
          offerAccepted,
        })
      ).data,
    onSuccess: (order) => {
      haptic.success();
      void queryClient.invalidateQueries({ queryKey: ['cart'] });
      void queryClient.invalidateQueries({ queryKey: ['cart-count'] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      navigate(`/orders/${order.id}`, { replace: true });
    },
    onError: (e) => {
      haptic.error();
      setError(errorMessage(e));
    },
  });

  const ready =
    offerAccepted &&
    Boolean(preview.data?.canCheckout) &&
    (fulfillment === 'PICKUP'
      ? Boolean(pickupPointId)
      : Boolean(addressId) && preview.data?.deliveryAvailable !== false);

  useEffect(() => {
    if (!ready) return setMainButton(null);

    return setMainButton({
      text: `${t('checkout.submit')} · ${money(preview.data?.total ?? 0)}`,
      loading: submit.isPending,
      onClick: () => submit.mutate(),
    });
  }, [ready, preview.data?.total, submit.isPending, t]);

  if (!authorized) {
    return (
      <Screen title={t('checkout.title')}>
        <EmptyState title={t('common.outsideTelegram')} />
      </Screen>
    );
  }

  const field =
    'w-full rounded-xl bg-surface px-4 py-3 text-sm shadow-soft outline-none placeholder:text-muted';

  return (
    <Screen title={t('checkout.title')}>
      <section className="space-y-2">
        <p className="text-sm font-medium">{t('checkout.howToGet')}</p>
        <div className="flex gap-2">
          <Chip active={fulfillment === 'DELIVERY'} onClick={() => setFulfillment('DELIVERY')}>
            {t('checkout.delivery')}
          </Chip>
          <Chip active={fulfillment === 'PICKUP'} onClick={() => setFulfillment('PICKUP')}>
            {t('checkout.pickup')}
          </Chip>
        </div>
      </section>

      {fulfillment === 'DELIVERY' ? (
        <section className="space-y-2">
          <p className="text-sm font-medium">{t('checkout.address')}</p>

          {addresses.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <div className="space-y-2">
              {(addresses.data ?? []).map((address) => (
                <button
                  key={address.id}
                  onClick={() => {
                    haptic.select();
                    setAddressId(address.id);
                  }}
                  className={`w-full rounded-xl p-3 text-left text-sm ${
                    addressId === address.id ? 'bg-bordo/15 ring-1 ring-bordo' : 'bg-surface'
                  }`}
                >
                  {address.label && <span className="block font-medium">{address.label}</span>}
                  <span className="block">{address.addressLine}</span>
                  {address.distanceKm !== null && (
                    <span className="text-xs text-muted">{address.distanceKm} км от склада</span>
                  )}
                </button>
              ))}

              {!showAddressForm && (
                <Button
                  variant="secondary"
                  className="w-full"
                  onClick={() => setShowAddressForm(true)}
                >
                  + {t('checkout.addAddress')}
                </Button>
              )}
            </div>
          )}

          {showAddressForm && (
            <AddressForm
              pending={createAddress.isPending}
              onCancel={() => setShowAddressForm(false)}
              onSubmit={(payload) => createAddress.mutate(payload)}
            />
          )}

          {preview.data?.deliveryAvailable === false && (
            <p className="text-sm text-error">
              {preview.data.deliveryReason ?? t('checkout.deliveryUnavailable')}
            </p>
          )}
        </section>
      ) : (
        <section className="space-y-2">
          <p className="text-sm font-medium">{t('checkout.pickupPoint')}</p>
          <div className="space-y-2">
            {(points.data ?? []).map((point) => (
              <button
                key={point.id}
                onClick={() => {
                  haptic.select();
                  setPickupPointId(point.id);
                }}
                className={`w-full rounded-xl p-3 text-left text-sm ${
                  pickupPointId === point.id ? 'bg-bordo/15 ring-1 ring-bordo' : 'bg-surface'
                }`}
              >
                <span className="block font-medium">{localized(point.name)}</span>
                <span className="block text-muted">{localized(point.address)}</span>
                {point.phone && <span className="text-xs text-muted">{point.phone}</span>}
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="space-y-2">
        <p className="text-sm font-medium">{t('checkout.payment')}</p>
        <div className="flex flex-wrap gap-2">
          <Chip active={payment === 'CLICK'} onClick={() => setPayment('CLICK')}>
            {t('checkout.payClick')}
          </Chip>
          <Chip active={payment === 'PAYME'} onClick={() => setPayment('PAYME')}>
            {t('checkout.payPayme')}
          </Chip>
          <Chip active={payment === 'CASH'} onClick={() => setPayment('CASH')}>
            {t('checkout.payCash')}
          </Chip>
        </div>
        {payment === 'CASH' && <p className="text-xs text-muted">{t('checkout.cashHint')}</p>}
      </section>

      <section className="space-y-2">
        <p className="text-sm font-medium">{t('checkout.promo')}</p>
        <div className="flex gap-2">
          <input
            value={promoInput}
            onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
            placeholder="AUTUMN20"
            className={field}
          />
          <Button variant="secondary" onClick={() => setPromoCode(promoInput.trim() || null)}>
            {t('checkout.promoApply')}
          </Button>
        </div>
        {preview.data?.promo && (
          <p className={`text-sm ${preview.data.promo.valid ? 'text-bordo' : 'text-error'}`}>
            {preview.data.promo.valid ? t('checkout.promoApplied') : preview.data.promo.reason}
          </p>
        )}
      </section>

      <section className="space-y-2">
        <p className="text-sm font-medium">{t('checkout.comment')}</p>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          className={field}
        />
      </section>

      {preview.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : preview.data ? (
        <Card className="space-y-1">
          <Row label={t('cart.rent')} value={money(preview.data.subtotal)} />
          {preview.data.promoDiscount > 0 && (
            <Row label={t('checkout.promo')} value={`−${money(preview.data.promoDiscount)}`} />
          )}
          {fulfillment === 'DELIVERY' && (
            <Row
              label={t('checkout.delivery_cost')}
              hint={preview.data.distanceKm ? `${preview.data.distanceKm} км` : undefined}
              value={
                preview.data.deliveryCost === 0
                  ? t('checkout.deliveryFree')
                  : money(preview.data.deliveryCost)
              }
            />
          )}
          {preview.data.depositTotal > 0 && (
            <Row
              label={t('cart.deposit')}
              hint={
                preview.data.depositMode === 'CASH_ON_PICKUP'
                  ? t('checkout.cashHint')
                  : t('cart.depositHint')
              }
              value={
                preview.data.depositMode === 'CASH_ON_PICKUP'
                  ? `${money(preview.data.depositTotal)} *`
                  : money(preview.data.depositTotal)
              }
            />
          )}
          <div className="mt-2 flex items-baseline justify-between border-t border-line pt-2">
            <span className="font-medium">{t('cart.total')}</span>
            <span className="text-lg font-semibold">{money(preview.data.total)}</span>
          </div>
        </Card>
      ) : null}

      <section className="space-y-2">
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            checked={offerAccepted}
            onChange={(next) => {
              haptic.tap();
              setOfferAccepted(next);
            }}
          />
          <span>
            {t('checkout.offer')}{' '}
            <button
              type="button"
              onClick={() => setShowOffer((v) => !v)}
              className="text-bordo underline"
            >
              {t('checkout.offerRead')}
            </button>
          </span>
        </label>

        {showOffer && offer.data && (
          <Card className="max-h-64 overflow-auto text-xs leading-relaxed text-muted">
            {localized(offer.data.content)}
          </Card>
        )}

        {!offerAccepted && <p className="text-xs text-muted">{t('checkout.offerRequired')}</p>}
      </section>

      {error && <p className="text-center text-sm text-error">{error}</p>}

      {!insideTelegram && (
        <Button
          className="w-full"
          disabled={!ready || submit.isPending}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? t('checkout.submitting') : t('checkout.submit')}
        </Button>
      )}
    </Screen>
  );
}

/**
 * Ввод адреса. Координаты пока вводятся руками: ключа Yandex Maps ещё нет,
 * а без координат доставку не посчитать. С появлением ключа здесь встанет карта,
 * и остальной экран не изменится.
 */
function AddressForm({
  pending,
  onCancel,
  onSubmit,
}: {
  pending: boolean;
  onCancel: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ label: '', addressLine: '', lat: '', lng: '', comment: '' });

  const field = 'w-full rounded-xl bg-line px-4 py-3 text-sm outline-none placeholder:text-muted';

  return (
    <Card className="space-y-2">
      <input
        value={form.label}
        onChange={(e) => setForm({ ...form, label: e.target.value })}
        placeholder={t('checkout.addressLabel')}
        className={field}
      />
      <input
        value={form.addressLine}
        onChange={(e) => setForm({ ...form, addressLine: e.target.value })}
        placeholder={t('checkout.addressLine')}
        className={field}
      />
      <div className="flex gap-2">
        <input
          value={form.lat}
          onChange={(e) => setForm({ ...form, lat: e.target.value })}
          placeholder="41.311"
          inputMode="decimal"
          className={field}
        />
        <input
          value={form.lng}
          onChange={(e) => setForm({ ...form, lng: e.target.value })}
          placeholder="69.240"
          inputMode="decimal"
          className={field}
        />
      </div>
      <p className="text-xs text-muted">{t('checkout.coordsHint')}</p>
      <input
        value={form.comment}
        onChange={(e) => setForm({ ...form, comment: e.target.value })}
        placeholder={t('checkout.addressComment')}
        className={field}
      />

      <div className="flex gap-2 pt-1">
        <Button variant="secondary" className="flex-1" onClick={onCancel}>
          {t('catalog.reset')}
        </Button>
        <Button
          className="flex-1"
          disabled={pending || !form.addressLine.trim()}
          onClick={() =>
            onSubmit({
              label: form.label || undefined,
              addressLine: form.addressLine,
              lat: form.lat ? Number(form.lat) : undefined,
              lng: form.lng ? Number(form.lng) : undefined,
              comment: form.comment || undefined,
            })
          }
        >
          {t('checkout.save')}
        </Button>
      </div>
    </Card>
  );
}
